import { Suspense, lazy, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { logEvent } from '@firebase/analytics';
import { Fallback } from 'components';
import { analytics } from 'config/firebase';
import { LOGIN_ROUTE } from 'commons/constants/app';
import { useUserData } from 'commons/contexts';
import storage from 'commons/utils/storage';

import { Route as TRoute } from 'commons/types/routes';
import { REDIRECT_AFTER_AUTH } from 'commons/constants/store';
import routes from './Routes';

const Login = lazy(() => import('../pages/Auth/Login'));
const Forbidden = lazy(() => import('../pages/Auth/Forbidden'));

const PrivateRoutes = () => {
  const { user } = useUserData();
  const navigate = useNavigate();
  const isAuthenticated = user.authenticated;

  useEffect(() => {
    if (!isAuthenticated && window.location.pathname !== '/auth') {
      const redirectPath = window.location.pathname + window.location.search + window.location.hash;
      storage.add(REDIRECT_AFTER_AUTH, redirectPath);
      navigate(LOGIN_ROUTE);
    }
  }, [isAuthenticated, navigate]);

  return routes;
};

function Page({ route }: { route: TRoute }) {
  useEffect(() => {
    document.title = route.title ? `Com.Figura - ${route.title}` : 'Com.Figura';
  });

  return (
    <Suspense fallback={<Fallback />}>
      <route.component />
    </Suspense>
  );
}

function Main() {
  const { user, hasResource } = useUserData();
  const routerLocation = useLocation();

  const renderRoutes = [
    ...PrivateRoutes(),
    {
      path: LOGIN_ROUTE,
      component: Login,
      permission: [],
      title: '',
    },
    {
      path: '*',
      component: Forbidden,
      permission: [],
    },
  ] as TRoute[];

  useEffect(() => {
    if (routerLocation.pathname !== '/' && user.profile.code != '') {
      logEvent(analytics, 'pagina_acessada', {
        [user.profile.code]: routerLocation.pathname,
      });
    }
  }, [routerLocation.pathname]);

  return (
    <Routes>
      {renderRoutes.map(route => {
        const isPublic = route.permission?.length === 0;
        const requiresPermission = route?.permission?.length && hasResource(route.permission) && user.authenticated;

        if (!requiresPermission && !isPublic) {
          return null;
        }

        return <Route {...route} key={route.path} element={<Page route={route} />} />;
      })}
    </Routes>
  );
}

export default Main;
